import React from "react";
import { Card, Stack, TextInput, Group, Button, Alert, Code, Text, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import { usePostMultiplacerProjectEditProjectMutation } from "../../openapi";
import { IFileAttrField } from "@backend/src/schemas";
import { useAppSelector } from "../app/hooks";
import { selectHttpCurrentProjct } from "../projectSelectors";

type FormValues = {
  projectNo: number | string;
  title: string;
  compCodePath: string;
  pnsPath: string;
  areaPath: string;
  mmiPath: string; // ✅ NEW
  disciplineFileAttr: IFileAttrField;
};

const ProjectBasicsCard: React.FC = () => {
  const project = useAppSelector(selectHttpCurrentProjct);
  const [editProject, { isLoading, isError, error, isSuccess }] =
    usePostMultiplacerProjectEditProjectMutation();

  const form = useForm<FormValues>({
    initialValues: {
      projectNo: project?.projectNo ?? "",
      title: project?.title ?? "",
      compCodePath: project?.compCodePath ?? "",
      pnsPath: project?.pnsPath ?? "",
      areaPath: project?.areaPath ?? "",
      mmiPath: (project as any)?.mmiPath ?? "", // ✅ NEW (bruk any hvis frontend-typen ikke er oppdatert ennå)
      disciplineFileAttr: project?.disciplineFileAttr ?? "fileAttr1",
    },
    validate: {
      title: (v) => (v?.length >= 3 ? null : "Minst 3 tegn"),
      compCodePath: (v) => (v ? null : "Påkrevd"),
      pnsPath: (v) => (v ? null : "Påkrevd"),
      areaPath: (v) => (v ? null : "Påkrevd"),
      mmiPath: (v) => (v ? null : "Påkrevd"), // ✅ NEW
    },
  });

  const handleSubmit = (values: FormValues) => {
    const payload = {
      project: {
        projectNo: project?.projectNo ?? values.projectNo,
        title: values.title,
        compCodePath: values.compCodePath,
        pnsPath: values.pnsPath, // ✅ (var savnet før)
        areaPath: values.areaPath,
        mmiPath: values.mmiPath, // ✅ NEW
        disciplineFileAttr: values.disciplineFileAttr || undefined,
      },
    } as const;

    editProject({ body: payload });
  };

  return (
    <Card withBorder radius="lg" p="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Text size="sm" c="dimmed">
            Oppdater metadata for prosjektet. <Code>projectNo</Code> hentes fra URL-en og sendes
            automatisk.
          </Text>

          {isError && (
            <Alert color="red" title="Kunne ikke lagre" variant="light">
              {(error as any)?.data?.message || "Det oppstod en feil under lagring."}
            </Alert>
          )}
          {isSuccess && <Alert color="green">Lagret!</Alert>}

          <TextInput label="Tittel" placeholder="Rikshospitalet" {...form.getInputProps("title")} />

          <Group grow align="flex-start">
            <TextInput label="Søkesti til compCode" {...form.getInputProps("compCodePath")} />
            <TextInput label="Søkesti til pns" {...form.getInputProps("pnsPath")} />
            <TextInput label="Søkesti til area" {...form.getInputProps("areaPath")} />
            <TextInput label="Søkesti til MMI" {...form.getInputProps("mmiPath")} />
          </Group>

          <Select
            label="Disciplin i fileAttr"
            placeholder="Velg hvilken fileAttr som inneholder disciplin"
            data={[
              { value: "fileAttr1", label: "fileAttr1" },
              { value: "fileAttr2", label: "fileAttr2" },
              { value: "fileAttr3", label: "fileAttr3" },
              { value: "fileAttr4", label: "fileAttr4" },
            ]}
            {...form.getInputProps("disciplineFileAttr")}
            allowDeselect={false}
          />

          <Group justify="flex-end">
            <Button type="submit" loading={isLoading}>
              Lagre
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
};

export default ProjectBasicsCard;
